import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import Image from "next/image";
import { Tooltip } from "react-tooltip";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import DropVoteProgressing from "@/components/drops/view/utils/DropVoteProgressing";
import ParticipationDropVoteDetailsTrigger from "@/components/waves/drops/participation/ratings/ParticipationDropVoteDetailsTrigger";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import { WAVE_VOTING_LABELS, WAVE_VOTE_STATS_LABELS } from "@/helpers/waves/waves.constants";
interface WaveDropRatingsProps {
  readonly drop: ApiDrop;
}
const getFallbackAvatarLabel = (label: string) => {
  const withoutHexPrefix = label.replace(/^0x/i, "");
  const source = withoutHexPrefix || label;
  return source.slice(0, 2).toUpperCase();
};
const WaveDropRatings: React.FC<WaveDropRatingsProps> = t0 => {
  const $ = _c(42);
  const {
    drop
  } = t0;
  const [activeRaterId, setActiveRaterId] = React.useState(null);
  const totalVote = drop.rating;
  const userVote = drop.context_profile_context?.rating ?? 0;
  const hasUserVoted = userVote !== 0;
  const votingLabel = WAVE_VOTING_LABELS[drop.wave.voting_credit_type];
  const totalVoteClass = totalVote < 0 ? "tw-text-rose-400" : "tw-text-iron-50";
  const userVoteClass = userVote < 0 ? "tw-text-rose-400" : "tw-text-iron-50";
  const t1 = `tw-font-medium ${totalVoteClass}`;
  let t2;
  if ($[0] !== totalVote) {
    t2 = formatNumberWithCommas(totalVote);
    $[0] = totalVote;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <span className={t1}>{t2}</span>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== drop.rating || $[6] !== drop.rating_prediction) {
    t4 = <DropVoteProgressing current={drop.rating} projected={drop.rating_prediction} compact={true} />;
    $[5] = drop.rating;
    $[6] = drop.rating_prediction;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <div className="tw-relative tw-inline-flex tw-items-center tw-gap-x-1.5">{t3}{t4}</div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== votingLabel) {
    t6 = <span className="tw-whitespace-nowrap tw-font-normal tw-text-iron-400">{votingLabel} {WAVE_VOTE_STATS_LABELS.TOTAL}</span>;
    $[11] = votingLabel;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <div className="tw-flex tw-items-center tw-gap-x-2">{t5}{t6}</div>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== activeRaterId || $[17] !== drop.id || $[18] !== drop.top_raters || $[19] !== votingLabel) {
    let t9;
    if ($[21] !== activeRaterId || $[22] !== drop.id || $[23] !== drop.top_raters.length || $[24] !== votingLabel) {
      t9 = (rater, index) => (() => {
        const raterLabel = rater.profile.handle ?? rater.profile.primary_address;
        const raterHref = `/${raterLabel}`;
        const tooltipId = `wave-drop-rater-${drop.id}-${rater.profile.id}`;
        const fallbackAvatarLabel = getFallbackAvatarLabel(raterLabel);
        const raterTooltipLabel = `${raterLabel} • ${formatNumberWithCommas(rater.rating)} ${votingLabel}`;
        const baseZIndex = drop.top_raters.length - index;
        const zIndex = activeRaterId === rater.profile.id ? drop.top_raters.length + 1 : baseZIndex;
        return <React.Fragment key={rater.profile.id}><Link href={raterHref} onClick={_temp} onMouseEnter={() => setActiveRaterId(rater.profile.id)} onMouseLeave={() => setActiveRaterId(null)} onFocus={() => setActiveRaterId(rater.profile.id)} onBlur={() => setActiveRaterId(null)} aria-label={raterTooltipLabel} title={raterTooltipLabel} data-tooltip-id={tooltipId} className="tw-relative tw-block tw-transition-transform hover:tw-scale-110 focus-visible:tw-rounded-md focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-400/70" style={{
            zIndex
          }}>{rater.profile.pfp ? <Image src={getScaledImageUri(rater.profile.pfp, ImageScale.W_AUTO_H_50)} alt={`${raterLabel}'s avatar`} width={20} height={20} className="tw-h-5 tw-w-5 tw-rounded-md tw-bg-iron-800 tw-object-contain tw-ring-1 tw-ring-black" /> : <span aria-hidden="true" className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-md tw-bg-iron-800 tw-text-[9px] tw-font-semibold tw-uppercase tw-text-iron-300 tw-ring-1 tw-ring-black">{fallbackAvatarLabel}</span>}</Link><Tooltip id={tooltipId} place="top" offset={8} opacity={1} positionStrategy="fixed" style={TOOLTIP_STYLES}>{raterLabel} • {formatNumberWithCommas(rater.rating)}{" "}{votingLabel}</Tooltip></React.Fragment>;
      })();
      $[21] = activeRaterId;
      $[22] = drop.id;
      $[23] = drop.top_raters.length;
      $[24] = votingLabel;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    t8 = drop.top_raters.map(t9);
    $[16] = activeRaterId;
    $[17] = drop.id;
    $[18] = drop.top_raters;
    $[19] = votingLabel;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[26] !== t8) {
    t9 = <div className="tw-flex tw-items-center -tw-space-x-1.5">{t8}</div>;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== drop) {
    t10 = <ParticipationDropVoteDetailsTrigger drop={drop} />;
    $[28] = drop;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t9) {
    t11 = <div className="tw-flex tw-items-center tw-gap-2 tw-whitespace-nowrap">{t9}{t10}</div>;
    $[30] = t10;
    $[31] = t9;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== hasUserVoted || $[34] !== userVote || $[35] !== userVoteClass || $[36] !== votingLabel) {
    t12 = hasUserVoted && <div className="tw-flex tw-items-center tw-gap-1.5"><span className="tw-whitespace-nowrap"><span className="tw-font-normal tw-text-iron-400">{WAVE_VOTE_STATS_LABELS.YOUR_VOTES}:{" "}</span><span className={`tw-font-medium ${userVoteClass}`}>{userVote < 0 && "-"}{formatNumberWithCommas(Math.abs(userVote))} {votingLabel}</span></span></div>;
    $[33] = hasUserVoted;
    $[34] = userVote;
    $[35] = userVoteClass;
    $[36] = votingLabel;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== t11 || $[39] !== t12 || $[40] !== t7) {
    t13 = <div className="tw-mt-1 tw-inline-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-text-sm tw-leading-5">{t7}{t11}{t12}</div>;
    $[38] = t11;
    $[39] = t12;
    $[40] = t7;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  return t13;
};
export default WaveDropRatings;
function _temp(event) {
  return event.stopPropagation();
}